import { Fragment, type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, MenuPopup, MenuTrigger } from "@/components/ui/menu";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons/icon-registry";
import { countVisibleActions } from "./count-visible-actions";

const MoreIcon = iconRegistry[ICON_KEYS.MORE_ACTIONS];

export interface ToolbarAction {
    key: string;
    /** Higher stays in the toolbar longer. */
    priority: number;
    button: ReactNode;
    menuItem: ReactNode;
}

/**
 * The range picker on the left; everything else on the right, where `pinned` always shows and the
 * actions move into a menu, lowest priority first, when the row runs out of width.
 */
export function ViewerToolbar({
    range,
    pinned,
    actions,
}: {
    range: ReactNode;
    pinned?: ReactNode;
    actions: ToolbarAction[];
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "toolbar" });
    const rowRef = useRef<HTMLDivElement | null>(null);
    const rangeRef = useRef<HTMLDivElement | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);
    const pinnedRef = useRef<HTMLDivElement | null>(null);
    const probeRef = useRef<HTMLButtonElement | null>(null);
    const [visibleCount, setVisibleCount] = useState(actions.length);

    useLayoutEffect(() => {
        const row = rowRef.current;
        const rangeGroup = rangeRef.current;
        const probe = probeRef.current;
        if (!row || !rangeGroup || !probe) return undefined;

        const measure = () => {
            const widths = {
                available: row.clientWidth,
                start: rangeGroup.getBoundingClientRect().width,
                endPinned: pinnedRef.current?.getBoundingClientRect().width ?? 0,
                button: probe.getBoundingClientRect().width,
                gap: parseFloat(getComputedStyle(row).columnGap),
                endGap: endRef.current ? parseFloat(getComputedStyle(endRef.current).columnGap) : 0,
            };
            setVisibleCount(countVisibleActions(widths, actions.length));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(row);
        observer.observe(rangeGroup);
        observer.observe(probe);
        if (pinnedRef.current) observer.observe(pinnedRef.current);
        return () => observer.disconnect();
    }, [actions.length]);

    const kept = new Set(
        [...actions]
            .sort((a, b) => b.priority - a.priority)
            .slice(0, visibleCount)
            .map((action) => action.key),
    );
    const shown = actions.filter((action) => kept.has(action.key));
    const moved = actions.filter((action) => !kept.has(action.key));

    return (
        <div className={"relative flex items-center gap-2 border-b pb-[16px]"} ref={rowRef}>
            {/* Sizes one action button, so the count works whether or not any are showing. */}
            <Button
                aria-hidden
                className={"invisible absolute"}
                ref={probeRef}
                size={"icon"}
                tabIndex={-1}
                variant={"outline"}
            />
            <div className={"flex items-center"} ref={rangeRef}>
                {range}
            </div>
            {(pinned || actions.length > 0) && (
                <div className={"ms-auto flex items-center gap-1"} ref={endRef}>
                    {pinned && (
                        <div className={"flex items-center gap-1"} ref={pinnedRef}>
                            {pinned}
                        </div>
                    )}
                    {shown.map((action) => (
                        <Fragment key={action.key}>{action.button}</Fragment>
                    ))}
                    {moved.length > 0 && (
                        <Menu>
                            <MenuTrigger
                                render={
                                    <Button
                                        aria-label={t("more-actions")}
                                        size={"icon"}
                                        tooltip={t("more-actions")}
                                        variant={"outline"}
                                    />
                                }
                            >
                                <MoreIcon />
                            </MenuTrigger>
                            <MenuPopup align={"end"}>
                                {moved.map((action) => (
                                    <Fragment key={action.key}>{action.menuItem}</Fragment>
                                ))}
                            </MenuPopup>
                        </Menu>
                    )}
                </div>
            )}
        </div>
    );
}
